// src/services/settingsService.js
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  serverTimestamp,
  arrayUnion,
  arrayRemove 
} from 'firebase/firestore';
import { 
  getAuth, 
  updateEmail, 
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider
} from 'firebase/auth';
import { db } from '../firebase';

const SETTINGS_DOC = 'app_settings';
const COLLECTION_NAME = 'settings';

// Initialize default settings if they don't exist
export const initializeSettings = async () => {
  try {
    const settingsRef = doc(db, COLLECTION_NAME, SETTINGS_DOC);
    const docSnap = await getDoc(settingsRef);
    
    if (!docSnap.exists()) {
      const defaultSettings = {
        companyInfo: {
          name: 'Apex Interio',
          email: 'info@apexinterio.in',
          phone: '+91 9108602000',
          address: 'Your Address Here',
          logo: null,
          signature: null,
          favicon: null
        },
        theme: {
          primaryColor: '#3b82f6', // Blue-600
          accentColor: '#4f46e5', // Indigo-600
          isDarkMode: false
        },
        roomTypes: [
          "LIVING", "KITCHEN", "DINING", "KBR1", "GBR", "MBR1", "MBR2", 
          "STUDY ROOM", "BALCONY 1", "BALCONY 2", "OTHERS", "BALCONY 3", "SERVICES"
        ],
        terms: {
          paymentTerms: [
            "10% of the quotation for design meetings and booking confirmation.",
            "50% upon signing of the contract.",
            "40% before delivery of the modular furnitures at the site.",
            "Amount once paid are non refundable."
          ],
          warrantyTerms: "We provide a 10-year warranty on all woodwork against manufacturing or installation defects.",
          additionalTerms: ""
        },
        paymentInfo: {
          bankName: '',
          accountName: '',
          accountNumber: '',
          ifscCode: '',
          branchName: '',
          accountType: 'Current Account',
          upiId: '',
          qrCode: null
        },
        emailSettings: {
          enabled: false,
          provider: 'smtp',
          host: '',
          port: '587',
          secure: false,
          username: '',
          password: '',
          fromEmail: '',
          fromName: '',
          sendTestEmail: false
        },
        pdfSettings: {
          font: 'NotoSans',
          coverPageEnabled: true,
          coverPageContent: {
            greeting: 'Greetings from',
            intro: 'At [Company], our team of creative professionals ensures that designs are beautiful and functional.\n\nWe are pioneers to have a vertically integrated in-house production unit, utilizing class automation technology and innovation. Our partnership with quality players like Hettich, Airolam/Stylam, etc., ensures that we rank first in class and high on quality products.\n\nWe have designed and delivered over 2000+ exquisitely designed homes inclusive of Apartments, Bungalows, Duplexes, Villas, and Villaments too, making us one the fastest growing design firms in Bangalore.\n\nAll these achievements would have been possible without our dedicated pool of highly efficient people inclusive of in-house designers who provide individual attention to all our client\'s prerequisites so that every home ends up as a beautiful #decorhome.',
            advantages: [
              {
                title: 'UNMATCHED PRICES',
                description: 'We assure you of never heard before prices for the given quality & services.',
                icon: '',
                enabled: true
              },
              {
                title: 'QUALITY & CREATIVITY',
                description: 'We use quality materials. Plywood, Hettich fittings, Airolam / Stylam etc.',
                icon: '',
                enabled: true
              },
              {
                title: 'WARRANTY',
                description: 'We provide exclusive warranty of 10 years.',
                icon: '',
                enabled: true
              },
              {
                title: 'TIMELY EXECUTION',
                description: 'Assured firm turnout time.',
                icon: '',
                enabled: true
              }
            ],
            closingText: 'Referring to our discussions, kindly find attached our proposal for designing your lovely home and in the meantime, kindly go through our website www.decorpot.com for completed projects by us; we are sure you will end up loving our work like our other customers have!'
          }
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      await setDoc(settingsRef, defaultSettings);
      console.log("Default settings initialized");
      return defaultSettings;
    }
    
    return docSnap.data();
  } catch (error) {
    console.error("Error initializing settings: ", error);
    throw error;
  }
};

// Get all settings
export const getSettings = async () => {
  try {
    const settingsRef = doc(db, COLLECTION_NAME, SETTINGS_DOC);
    const docSnap = await getDoc(settingsRef);
    
    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      return await initializeSettings();
    }
  } catch (error) {
    console.error("Error getting settings: ", error);
    throw error;
  }
};

// Update company information
export const updateCompanyInfo = async (companyInfo) => {
  try {
    const settingsRef = doc(db, COLLECTION_NAME, SETTINGS_DOC);
    await updateDoc(settingsRef, {
      companyInfo,
      updatedAt: serverTimestamp()
    });
    return companyInfo;
  } catch (error) {
    console.error("Error updating company info: ", error);
    throw error;
  }
};

// Update theme settings
export const updateTheme = async (theme) => {
  try {
    const settingsRef = doc(db, COLLECTION_NAME, SETTINGS_DOC);
    await updateDoc(settingsRef, {
      theme,
      updatedAt: serverTimestamp()
    });
    return theme;
  } catch (error) {
    console.error("Error updating theme: ", error);
    throw error;
  }
};

// Get room types
export const getRoomTypes = async () => {
  try {
    const settings = await getSettings();
    return settings.roomTypes || [];
  } catch (error) {
    console.error("Error getting room types: ", error);
    // Return default room types if there's an error
    return [
      "LIVING", "KITCHEN", "DINING", "KBR1", "GBR", "MBR1", "MBR2", 
      "STUDY ROOM", "BALCONY 1", "BALCONY 2", "OTHERS", "BALCONY 3", "SERVICES"
    ];
  }
};

// Add a new room type
export const addRoomType = async (roomType) => {
  try {
    // Convert to uppercase for consistency
    const formattedRoomType = roomType.toUpperCase().trim();
    const settingsRef = doc(db, COLLECTION_NAME, SETTINGS_DOC);
    await updateDoc(settingsRef, {
      roomTypes: arrayUnion(formattedRoomType),
      updatedAt: serverTimestamp()
    });
    return formattedRoomType;
  } catch (error) {
    console.error("Error adding room type: ", error);
    throw error;
  }
};

// Remove a room type
export const removeRoomType = async (roomType) => {
  try {
    const settingsRef = doc(db, COLLECTION_NAME, SETTINGS_DOC);
    await updateDoc(settingsRef, {
      roomTypes: arrayRemove(roomType),
      updatedAt: serverTimestamp()
    });
    return roomType;
  } catch (error) {
    console.error("Error removing room type: ", error);
    throw error;
  }
};

// Get terms and conditions settings
export const getTermsSettings = async () => {
  try {
    const settings = await getSettings();
    return settings.terms || {
      paymentTerms: [
        "10% of the quotation for design meetings and booking confirmation.",
        "50% upon signing of the contract.",
        "40% before delivery of the modular furnitures at the site.",
        "Amount once paid are non refundable."
      ],
      warrantyTerms: "We provide a 10-year warranty on all woodwork against manufacturing or installation defects.",
      additionalTerms: ""
    };
  } catch (error) {
    console.error("Error getting terms settings: ", error);
    throw error;
  }
};

// Update terms and conditions settings
export const updateTermsSettings = async (termsData) => {
  try {
    const settingsRef = doc(db, COLLECTION_NAME, SETTINGS_DOC);
    await updateDoc(settingsRef, {
      terms: termsData,
      updatedAt: serverTimestamp()
    });
    return termsData;
  } catch (error) {
    console.error("Error updating terms settings: ", error);
    throw error;
  }
};

// Get payment information
export const getPaymentInfo = async () => {
  try {
    const settings = await getSettings();
    return settings.paymentInfo || {
      bankName: '',
      accountName: '',
      accountNumber: '',
      ifscCode: '',
      branchName: '',
      accountType: 'Current Account',
      upiId: '',
      qrCode: null
    };
  } catch (error) {
    console.error("Error getting payment info: ", error);
    throw error;
  }
};

// Update payment information
export const updatePaymentInfo = async (paymentInfo) => {
  try {
    const settingsRef = doc(db, COLLECTION_NAME, SETTINGS_DOC);
    await updateDoc(settingsRef, {
      paymentInfo,
      updatedAt: serverTimestamp()
    });
    return paymentInfo;
  } catch (error) {
    console.error("Error updating payment info: ", error);
    throw error;
  }
};

// Update user profile (requires auth)
export const updateUserProfile = async (data) => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error("No authenticated user found");
    }
    
    // Update email if provided and different
    if (data.email && data.email !== user.email) {
      await updateEmail(user, data.email);
    }
    
    return { success: true };
  } catch (error) {
    console.error("Error updating user profile: ", error);
    throw error;
  }
};

// Change password (requires auth)
export const changePassword = async (currentPassword, newPassword) => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error("No authenticated user found");
    }
    
    // Re-authenticate user before changing password
    const credential = EmailAuthProvider.credential(
      user.email,
      currentPassword
    );
    
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
    
    return { success: true };
  } catch (error) {
    console.error("Error changing password: ", error);
    throw error;
  }
};

// Upload company logo (placeholder - this would normally use Firebase Storage)
export const uploadLogo = async (logoFile) => {
  // This would normally use Firebase Storage, but we'll just simulate it with a base64 encoding for now
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(logoFile);
    reader.onload = async () => {
      try {
        const base64Logo = reader.result;
        
        // Update the company info with the new logo
        const settings = await getSettings();
        const updatedCompanyInfo = {
          ...settings.companyInfo,
          logo: base64Logo
        };
        
        await updateCompanyInfo(updatedCompanyInfo);
        resolve(base64Logo);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = (error) => {
      reject(error);
    };
  });
};

// Upload signature image
export const uploadSignature = async (signatureFile) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(signatureFile);
    reader.onload = async () => {
      try {
        const base64Signature = reader.result;
        
        // Update the company info with the new signature
        const settings = await getSettings();
        const updatedCompanyInfo = {
          ...settings.companyInfo,
          signature: base64Signature
        };
        
        await updateCompanyInfo(updatedCompanyInfo);
        resolve(base64Signature);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = (error) => {
      reject(error);
    };
  });
};

// Update PDF settings
export const updatePDFSettings = async (pdfSettings) => {
  try {
    const settingsRef = doc(db, COLLECTION_NAME, SETTINGS_DOC);
    await updateDoc(settingsRef, {
      pdfSettings,
      updatedAt: serverTimestamp()
    });
    return pdfSettings;
  } catch (error) {
    console.error("Error updating PDF settings: ", error);
    throw error;
  }
};

// Update email notification settings
export const updateEmailSettings = async (emailSettings) => {
  try {
    console.log("Updating email settings");
    const settingsRef = doc(db, COLLECTION_NAME, SETTINGS_DOC);
    
    // If sending test email is requested, call the Brevo API
    if (emailSettings.sendTestEmail && emailSettings.testEmailRecipient) {
      console.log("Attempting to send test email to:", emailSettings.testEmailRecipient);
      
      try {
        // Create a temporary configuration with sensitive data masked in the logs
        const logSafeConfig = {
          ...emailSettings,
          password: emailSettings.password ? '******' : '',
          testEmailRecipient: emailSettings.testEmailRecipient
        };
        console.log("Email test configuration:", logSafeConfig);
        
        // Get company info for the email content
        const settings = await getSettings();
        const companyInfo = settings.companyInfo || { name: 'Your Company' };
        
        // For Brevo API - send an actual email
        if (emailSettings.provider === 'brevo') {
          const brevoResponse = await sendBrevoEmail(
            emailSettings.username, // API key
            emailSettings.fromEmail || companyInfo.email,
            emailSettings.fromName || companyInfo.name,
            emailSettings.testEmailRecipient,
            "Test Email from Apex Interio Quotation System",
            `<html><body>
              <h1>Test Email</h1>
              <p>This is a test email from your Apex Interio Quotation System.</p>
              <p>Your email settings are working correctly!</p>
              <p>Regards,<br>${emailSettings.fromName || companyInfo.name}</p>
            </body></html>`
          );
          
          console.log("Brevo API response:", brevoResponse);
          
          if (!brevoResponse.ok) {
            throw new Error(`Brevo API error: ${brevoResponse.status} - ${brevoResponse.errorText}`);
          }
        } 
        // For SMTP - we can't directly use SMTP from browser, so we'll simulate it
        else if (emailSettings.provider === 'smtp') {
          // In a production environment, this would call a server endpoint or Cloud Function
          console.log("SMTP sending from browser isn't supported directly. Consider using a server endpoint.");
          
          // Simulate a successful send for testing purposes
          await new Promise(resolve => setTimeout(resolve, 1000));
          console.log("Simulated SMTP email send (would be implemented on server)");
        }
        
        // Reset the send test flag to avoid sending it again on next update
        emailSettings = {
          ...emailSettings,
          sendTestEmail: false
        };
      } catch (testError) {
        console.error("Error sending test email:", testError);
        throw new Error(`Failed to send test email: ${testError.message}`);
      }
    }
    
    // Update the settings document with the new email settings
    await updateDoc(settingsRef, {
      emailSettings,
      updatedAt: serverTimestamp()
    });
    
    console.log("Email settings updated successfully");
    return emailSettings;
  } catch (error) {
    console.error("Error updating email settings:", error);
    throw error;
  }
};

// Function to send emails using Brevo API
export async function sendBrevoEmail(apiKey, fromEmail, fromName, toEmail, subject, htmlContent) {
  console.log(`Sending email via Brevo from ${fromEmail} to ${toEmail}`);
  
  const url = 'https://api.sendinblue.com/v3/smtp/email';
  
  const data = {
    sender: {
      name: fromName,
      email: fromEmail
    },
    to: [
      {
        email: toEmail,
        name: toEmail.split('@')[0]
      }
    ],
    subject: subject,
    htmlContent: htmlContent
  };
  
  try {
    // Log the request details (excluding API key for security)
    console.log("Brevo API request:", {
      url,
      method: 'POST',
      data: JSON.stringify(data),
      apiKeyProvided: !!apiKey
    });
    
    // Check if API key is provided
    if (!apiKey || apiKey.trim() === '') {
      return {
        ok: false,
        status: 400,
        errorText: "API key is missing or empty"
      };
    }
    
    // Make the API call
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'api-key': apiKey
      },
      body: JSON.stringify(data)
    });
    
    console.log("Brevo API response status:", response.status);
    
    // Create a result object that includes status and any error message
    const result = {
      ok: response.ok,
      status: response.status
    };
    
    // If response is not ok, read the error text and include it in the result
    if (!response.ok) {
      const responseBody = await response.json().catch(() => null);
      const errorText = responseBody ? JSON.stringify(responseBody) : await response.text();
      console.error("Brevo API error:", errorText);
      result.errorText = errorText;
    } else {
      const successText = await response.text();
      console.log("Brevo API success:", successText);
    }
    
    return result;
  } catch (error) {
    console.error("Error sending Brevo email:", error);
    return {
      ok: false,
      status: 500,
      errorText: error.message
    };
  }
}

// Upload favicon image
export const uploadFavicon = async (faviconFile) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(faviconFile);
    reader.onload = async () => {
      try {
        const base64Favicon = reader.result;
        
        // Update the company info with the new favicon
        const settings = await getSettings();
        const updatedCompanyInfo = {
          ...settings.companyInfo,
          favicon: base64Favicon
        };
        
        await updateCompanyInfo(updatedCompanyInfo);
        
        // Apply the favicon immediately
        const favicon = document.querySelector('link[rel="icon"]');
        if (favicon) {
          favicon.href = base64Favicon;
        } else {
          const newFavicon = document.createElement('link');
          newFavicon.rel = 'icon';
          newFavicon.href = base64Favicon;
          document.head.appendChild(newFavicon);
        }
        
        resolve(base64Favicon);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = (error) => {
      reject(error);
    };
  });
};

// Function to send emails with attachments using Brevo API
export async function sendBrevoEmailWithAttachment(apiKey, fromEmail, fromName, toEmail, subject, htmlContent, attachments = []) {
  console.log(`Sending email with attachment via Brevo from ${fromEmail} to ${toEmail}`);
  
  const url = 'https://api.sendinblue.com/v3/smtp/email';
  
  const data = {
    sender: {
      name: fromName,
      email: fromEmail
    },
    to: [
      {
        email: toEmail,
        name: toEmail.split('@')[0]
      }
    ],
    subject: subject,
    htmlContent: htmlContent
  };
  
  // Add attachments if provided
  if (attachments && attachments.length > 0) {
    data.attachment = attachments;
  }
  
  try {
    // Log the request details (excluding API key for security)
    console.log("Brevo API request with attachment:", {
      url,
      method: 'POST',
      data: {
        ...data,
        attachment: data.attachment ? `${data.attachment.length} attachment(s)` : 'No attachments'
      },
      apiKeyProvided: !!apiKey
    });
    
    // Check if API key is provided
    if (!apiKey || apiKey.trim() === '') {
      return {
        ok: false,
        status: 400,
        errorText: "API key is missing or empty"
      };
    }
    
    // Make the API call
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'api-key': apiKey
      },
      body: JSON.stringify(data)
    });
    
    console.log("Brevo API response status:", response.status);
    
    // Create a result object that includes status and any error message
    const result = {
      ok: response.ok,
      status: response.status
    };
    
    // If response is not ok, read the error text and include it in the result
    if (!response.ok) {
      const responseBody = await response.json().catch(() => null);
      const errorText = responseBody ? JSON.stringify(responseBody) : await response.text();
      console.error("Brevo API error:", errorText);
      result.errorText = errorText;
    } else {
      const successText = await response.text();
      console.log("Brevo API success:", successText);
    }
    
    return result;
  } catch (error) {
    console.error("Error sending Brevo email with attachment:", error);
    return {
      ok: false,
      status: 500,
      errorText: error.message
    };
  }
}